import React, { useState, useEffect } from 'react';
import api from '../utils/api';

interface PromotionalPictures {
  mainCard: {
    title: string;
    subtitle: string;
    imageUrl: string;
    
  };
  secondaryCards: [
    {
      title: string;
      subtitle: string;
      imageUrl: string;
    },
    {
      title: string;
      subtitle: string;
      imageUrl: string;
    }
  ];
}

const getBannerImageUrl = (imagePath?: string): string => {
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    return '';
  }

  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/\\/g, '/');
  const filename = normalizedPath.split('/').pop();
  if (!filename) return '';

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${apiUrl}/api/products/image/${filename}`;
};

const HeroBanner: React.FC = () => {
  const [promotionalPictures, setPromotionalPictures] = useState<PromotionalPictures | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromotionalPictures = async () => {
      try {
        const response = await api.get('/api/promotional-pictures/active');
        setPromotionalPictures(response.data);
      } catch (error) {
        console.error('Error fetching promotional pictures:', error);
        // Use default values if API fails
        setPromotionalPictures({
          mainCard: {
            title: 'المجموعة المميزة',
            subtitle: 'شموع صويا فاخرة',
            imageUrl: '',
            
          },
          secondaryCards: [
            {
              title: 'مجموعات هدايا',
              subtitle: 'مثالية للهدايا',
              imageUrl: ''
            },
            {
              title: 'إكسسوارات',
              subtitle: 'حاملات شموع وأكثر',
              imageUrl: ''
            }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPromotionalPictures();
  }, []);

  if (loading) {
    return (
      <div className="relative overflow-hidden bg-white dark:bg-dark-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600 dark:text-dark-textSecondary">جاري التحميل...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full transform -skew-y-1 bg-gradient-to-r from-pink-200 to-rose-200 dark:from-dark-surface dark:to-dark-hover"></div>
      </div>
      
      {/* Dripping Wax Effect */}
      <div className="absolute top-0 left-0 w-full h-20 opacity-30 bg-gradient-to-b from-pink-200 to-transparent dark:from-dark-surface dark:to-transparent"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 lg:gap-12 items-start">
          {/* Content */}
          <div className="text-center lg:text-right lg:-mt-4" dir="rtl">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-dark-text mb-4 md:mb-6 leading-tight">
              <span>شموع وتوزيعات </span>
              <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
                فاخرة
              </span>
            </h1>
            
            <p className="text-base md:text-xl text-gray-600 dark:text-dark-textSecondary mb-6 md:mb-8 leading-relaxed">
              استكشف منتجاتنا المتنوعة من شموع مصنوعة يدوياً وتوزيعات وغيرها من المنتجات المميزة
            </p>
            
            <div className="flex justify-center lg:justify-start">
              <button
                onClick={() => {
                  const productsSection = document.getElementById('products-section');
                  if (productsSection) {
                    productsSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-full hover:from-pink-600 hover:to-rose-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                تسوق الآن
                <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Features */}
            <div className="mt-10 md:mt-12 grid grid-cols-3 md:grid-cols-3 gap-3 md:gap-6 text-center">
              <div className="text-center lg:text-right">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-3">
                  <i className="fa-solid fa-tag text-2xl text-pink-600 dark:text-pink-400"></i>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text mb-1">خصومات</h3>
                <p className="text-sm text-gray-600 dark:text-dark-textSecondary">للطلبات أكثر من 200 شيقل</p>
              </div>
              
              <div className="text-center lg:text-right">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-3">
                  <i className="fa-solid fa-star text-2xl text-pink-600 dark:text-pink-400"></i>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text mb-1">جودة فاخرة</h3>
                <p className="text-sm text-gray-600 dark:text-dark-textSecondary">مصنوعة بعناية فائقة</p>
              </div>
              
              <div className="text-center lg:text-right">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-3">
                  <i className="fa-solid fa-gift text-2xl text-pink-600 dark:text-pink-400"></i>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text mb-1">هدايا مثالية</h3>
                <p className="text-sm text-gray-600 dark:text-dark-textSecondary">توزيعات متنوعة</p>
              </div>
            </div>
          </div>
          
          {/* Product Showcase */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {/* Main Product */}
              <div className="col-span-2 relative group">
                <div 
                  className="aspect-[5/4] md:aspect-[4/3] lg:aspect-[16/11] bg-gradient-to-br from-pink-200 to-rose-200 dark:from-amber-800 dark:to-orange-800 rounded-2xl shadow-2xl overflow-hidden"
                  style={{
                    backgroundImage: getBannerImageUrl(promotionalPictures?.mainCard.imageUrl)
                      ? `url(${getBannerImageUrl(promotionalPictures?.mainCard.imageUrl)})`
                      : undefined,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4" dir="rtl">
                    <h3 className="text-white font-bold text-lg mb-1">{promotionalPictures?.mainCard.title}</h3>
                    <p className="text-white/90 text-sm">{promotionalPictures?.mainCard.subtitle}</p>
                  </div>
                </div>
              </div>
              
              {/* Secondary Products */}
              <div className="relative group">
                <div 
                  className="aspect-square bg-gradient-to-br from-pink-100 to-rose-100 dark:from-amber-700 dark:to-orange-700 rounded-xl shadow-lg overflow-hidden"
                  style={{
                    backgroundImage: getBannerImageUrl(promotionalPictures?.secondaryCards[0]?.imageUrl)
                      ? `url(${getBannerImageUrl(promotionalPictures?.secondaryCards[0]?.imageUrl)})`
                      : undefined,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                  <div className="absolute bottom-2 left-2 right-2" dir="rtl">
                    <h4 className="text-white font-semibold text-sm">{promotionalPictures?.secondaryCards[0]?.title}</h4>
                    <p className="text-white/90 text-xs">{promotionalPictures?.secondaryCards[0]?.subtitle}</p>
                  </div>
                </div>
              </div>
              
              <div className="relative group">
                <div 
                  className="aspect-square bg-gradient-to-br from-pink-100 to-rose-100 dark:from-amber-700 dark:to-orange-700 rounded-xl shadow-lg overflow-hidden"
                  style={{
                    backgroundImage: getBannerImageUrl(promotionalPictures?.secondaryCards[1]?.imageUrl)
                      ? `url(${getBannerImageUrl(promotionalPictures?.secondaryCards[1]?.imageUrl)})`
                      : undefined,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                  <div className="absolute bottom-2 left-2 right-2" dir="rtl">
                    <h4 className="text-white font-semibold text-sm">{promotionalPictures?.secondaryCards[1]?.title}</h4>
                    <p className="text-white/90 text-xs">{promotionalPictures?.secondaryCards[1]?.subtitle}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements removed per request */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;

